import { useEffect, useState } from 'react';
import { Gem, Timer, CalendarCog } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { C, fontSerif, fontMono } from './utils';

export const IncentiveHeader = ({ views, activeView, onViewChange, periods, selectedPeriodId, onPeriodChange, period, canManagePeriods, onManagePeriods }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  let daysLeft = null;
  if (period?.end_date) {
    const endMs = new Date(period.end_date + 'T23:59:59').getTime();
    daysLeft = Math.max(Math.ceil((endMs - now) / 86400000), 0);
  }

  return (
    <div
      className="sticky top-0 z-30 -mx-6 mb-6 border-b px-6 py-4 md:-mx-10 md:px-10 lg:-mx-12 lg:px-12"
      style={{ background: 'rgba(250,248,245,0.9)', backdropFilter: 'blur(12px)', borderColor: C.border }}
      data-testid="incentive-header"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: C.burgundy }}>
              <Gem className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold leading-tight" style={{ fontFamily: fontSerif, color: C.text }}>
                Incentives
              </h1>
              <p className="text-xs text-slate-500">Daily standards · monthly output · rewards</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {daysLeft != null && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold"
                style={{ borderColor: C.border, background: C.card, color: C.burgundy }}
                data-testid="header-countdown-pill"
              >
                <Timer className="h-4 w-4" />
                <span style={{ fontFamily: fontMono }}>{daysLeft}</span> days left
              </span>
            )}
            <Select value={selectedPeriodId || ''} onValueChange={onPeriodChange}>
              <SelectTrigger className="w-44 bg-white" data-testid="period-selector">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                {(periods || []).map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`period-option-${p.id}`}>
                    {p.name} {p.status === 'OPEN' ? '· open' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canManagePeriods && (
              <button
                onClick={onManagePeriods}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition hover:bg-white"
                style={{ borderColor: C.border, color: C.burgundy }}
                title="Create or edit reward cycles"
                data-testid="manage-periods-button"
              >
                <CalendarCog className="h-4 w-4" />
                <span className="hidden sm:inline">Manage cycle</span>
              </button>
            )}
          </div>
        </div>

        <nav className="flex flex-wrap gap-1" data-testid="role-view-switch">
          {views.map((v) => {
            const Icon = v.icon;
            const active = activeView === v.id;
            return (
              <button
                key={v.id}
                onClick={() => onViewChange(v.id)}
                className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                  active ? 'text-white' : 'text-slate-600 hover:bg-white'
                }`}
                style={active ? { background: C.burgundy } : {}}
                data-testid={`view-tab-${v.id}`}
              >
                <Icon className="h-4 w-4" />
                {v.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
